import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { isNativeApp } from '../utils/deviceIdentity';
import { handleFormBack } from '../utils/formStack';
import { endSessionLog } from '../utils/session';

// HashRouter paths that are app "roots". Pressing the Android hardware back
// button here must NOT walk back into the previous (auth/OTP) history entry —
// instead we ask the user whether to exit the app.
//  - '/'               → Login (app entry)
//  - '/app'            → the index redirect target
//  - '/app/dashboard'  → admins land here (see App.jsx <Home>)
//  - '/app/members'    → plain users land here
const EXIT_PATHS = new Set(['/', '/app', '/app/dashboard', '/app/members']);

// Reach the Capacitor App plugin through the global plugin registry (same
// no-bundler-import pattern the app uses for Device in utils/deviceIdentity.js),
// so the plain web build is completely unaffected.
function getCapApp() {
  if (typeof window === 'undefined') return null;
  return window.Capacitor?.Plugins?.App || null;
}

export default function BackButtonHandler() {
  const navigate = useNavigate();
  const location = useLocation();
  const [askExit, setAskExit] = useState(false);

  // Keep the latest location in a ref so the (once-registered) listener always
  // reads the current route without re-subscribing on every navigation.
  const locRef = useRef(location);
  locRef.current = location;

  const exitApp = useCallback(() => {
    setAskExit(false);
    endSessionLog(); // stamp final logout time before quitting (best-effort)
    const App = getCapApp();
    if (App?.exitApp) App.exitApp();
  }, []);

  useEffect(() => {
    if (!isNativeApp()) return undefined;
    const App = getCapApp();
    if (!App?.addListener) return undefined;

    let handle;
    const sub = App.addListener('backButton', () => {
      // 1) An open add/edit form takes priority: filled → clear, empty → close.
      if (handleFormBack()) return;
      const path = locRef.current.pathname;
      if (EXIT_PATHS.has(path)) {
        setAskExit(true); // 3) root page → confirm exit
      } else {
        setAskExit(false);
        navigate(-1); // 2) inner page → normal back navigation
      }
    });
    // addListener may return a handle or a Promise<handle> depending on version.
    Promise.resolve(sub).then((h) => { handle = h; });

    return () => { if (handle && handle.remove) handle.remove(); };
  }, [navigate]);

  if (!askExit) return null;

  return (
    <div style={styles.overlay} role="dialog" aria-modal="true">
      <div style={styles.box}>
        <p style={styles.msg}>Do you want to exit?</p>
        <div style={styles.actions}>
          <button type="button" style={styles.btnGhost} onClick={() => setAskExit(false)}>No</button>
          <button type="button" style={styles.btnPrimary} onClick={exitApp}>Yes</button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed', inset: 0, zIndex: 9999,
    background: 'rgba(0,0,0,0.45)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 24,
  },
  box: {
    background: '#fff', borderRadius: 12, width: '100%', maxWidth: 320,
    padding: '22px 20px 16px', boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
    textAlign: 'center',
  },
  msg: { margin: '0 0 18px', fontSize: 16, fontWeight: 600, color: '#1f2937' },
  actions: { display: 'flex', gap: 10, justifyContent: 'center' },
  btnGhost: {
    flex: 1, padding: '10px 0', borderRadius: 8, border: '1px solid #d1d5db',
    background: '#fff', color: '#374151', fontSize: 15, fontWeight: 600, cursor: 'pointer',
  },
  btnPrimary: {
    flex: 1, padding: '10px 0', borderRadius: 8, border: 'none',
    background: '#dc2626', color: '#fff', fontSize: 15, fontWeight: 600, cursor: 'pointer',
  },
};
